import { Select, SelectItem } from '@heroui/react'
import type React from 'react'
import type { FC } from 'react'
import { useEffect } from 'react'
import { useSnapshot } from 'valtio'
import { state, useRadarNav } from '../../lib/state'

interface CommonRadarSelectorProps {
  onSelectRadar?: (radarId: number) => void
  onManageRadars?: () => void
  showAdminOptions?: boolean
  buttonClassName?: string
  buttonStyle?: React.CSSProperties
  dropdownClassName?: string
  dropdownItemClassName?: string
  buttonLabel?: string
}

export const CommonRadarSelector: FC<CommonRadarSelectorProps> = ({
  onSelectRadar,
  onManageRadars,
  showAdminOptions,
  buttonClassName,
  dropdownItemClassName,
  buttonLabel
}) => {
  // Ensure trigger background stays transparent on hover using data-hovered attribute
  useEffect(() => {
    const styleElement = document.createElement('style')
    styleElement.textContent = `
      .heroui-select [data-slot="trigger"],
      .heroui-select [data-slot="trigger"]:hover,
      .heroui-select [data-slot="trigger"]:focus,
      .heroui-select [data-slot="trigger"]:active,
      .heroui-select [data-slot="trigger"][data-hovered="true"],
      .heroui-select [data-slot="trigger"][data-hover="true"] {
        background: transparent !important;
        transition: none !important;
        box-shadow: none !important;
        outline: none !important;
      }
      .heroui-select [data-slot="trigger"][data-hovered="true"],
      .heroui-select [data-slot="trigger"][data-hover="true"] {
        border-color: #3b82f6 !important; /* Tailwind blue-500 - consistent across themes */
      }
    `
    document.head.appendChild(styleElement)
    return () => {
      document.head.removeChild(styleElement)
    }
  }, [])

  const { availableRadars } = useSnapshot(state)
  const { radarId } = useRadarNav()

  if (!availableRadars) return null

  // Determine the display label for the dropdown button
  const selectedRadar = availableRadars?.find(
    r => String(r.id) === String(radarId)
  )
  let displayName = selectedRadar?.name || buttonLabel || 'Select Radar'

  // For admin contexts, use a more specific fallback
  if (showAdminOptions && !selectedRadar?.name && !buttonLabel) {
    displayName = 'Change Radar'
  }

  // Always show the selector if showAdminOptions is true (e.g., to allow managing/adding radars)
  if (availableRadars?.length === 0 && !showAdminOptions) {
    return null
  }

  const baseRadarOptions = availableRadars?.map(radar => ({
    key: String(radar.id),
    label: radar.name
  }))

  const adminOptions = showAdminOptions
    ? [{ key: 'manage', label: 'Manage Radars' }]
    : []

  const radarOptions = [...baseRadarOptions, ...adminOptions]

  return (
    <Select
      aria-label="Radar selection"
      selectedKeys={radarId ? [String(radarId)] : []}
      selectionMode="single"
      onSelectionChange={keys => {
        const key = Array.from(keys)[0]
        if (key === 'manage' && onManageRadars) {
          onManageRadars()
        } else if (key) {
          onSelectRadar?.(Number(key))
        }
      }}
      variant="flat"
      // className={`h-8 ${dropdownClassName || ''}`}
      classNames={{
        base: 'bg-transparent rounded-sm',
        trigger: `h-7 min-h-0 px-2 py-0.5 text-black dark:text-white bg-transparent ${buttonClassName || ''} border border-[var(--ds-border)] hover:border-blue-500 hover:!bg-transparent data-[hover=true]:!bg-transparent items-center transition-none`,
        value: 'truncate',
        listboxWrapper: 'bg-[var(--ds-surface)]',
        popoverContent: 'bg-[var(--ds-surface)]'
      }}
      style={{ color: 'var(--ds-text)' }}
      renderValue={selected => {
        const key = selected?.[0]
        const option = radarOptions.find(opt => opt.key === key)
        return (
          <span style={{ color: 'var(--ds-text)' }}>
            {option ? option.label : displayName}
          </span>
        )
      }}
      placeholder={displayName}
      data-testid="common-radar-selector-button"
      fullWidth={false}
      radius="sm"
    >
      {radarOptions.map(option => (
        <SelectItem
          key={option.key}
          className={`${option.key === 'manage' ? 'text-warning' : 'dropdown-item'}  ${dropdownItemClassName || ''}`}
          classNames={{
            selectedIcon: 'flex items-center'
          }}
          data-testid={
            option.key === 'manage'
              ? 'common-radar-selector-manage'
              : `common-radar-selector-item-${option.key}`
          }
        >
          {option.label}
        </SelectItem>
      ))}
    </Select>
  )
}

export default CommonRadarSelector
